/**
 * Checkbox — the design system's soft-cornered checkbox.
 *
 * Ported from `tw-components.jsx`; `cx`/`Icon` are extracted imports and the file is
 * formatted for this repo.
 *
 * ─── One divergence: `relative` on the label ────────────────────────────────────
 *
 * Upstream's label carries no positioning, so the `absolute` input's containing block
 * is whatever positioned ancestor happens to be above it. In the listing filter rail
 * that is the **sticky `<aside>`** — the input resolves outside the 204px scroll
 * viewport it visually sits in.
 *
 * The input is also `h-0 w-0`, i.e. an empty rect. Clicking the label focuses it, the
 * browser scrolls a newly-focused element into view, and with an empty rect it cannot
 * resolve a position — it scrolls to the extreme instead. Measured on `/products` with
 * the brand list scrolled down: a real click took `window.scrollY` from **0 to 1339**
 * (= `maxScroll`) *synchronously with `focusin`*, while `scrollHeight` never changed.
 * That is a pure scroll, not a height collapse, and it only reproduces once the filter
 * list itself has been scrolled — which is why it survived several rounds of
 * height-related fixes.
 *
 * `relative` makes the label the containing block, so scroll-into-view targets a box
 * that is already visible and does nothing. Verified across list offsets 600/bottom ×
 * window offsets 0/400/1200. Alternatives that also worked: giving the input a 1px box,
 * or `inset-0 w-full h-full` — the second one is wrong, it overlays the label and kills
 * the `hover:border-orange-500` affordance on `dnd-check__box`.
 *
 * `position: relative` with no offsets is visually inert, so this is safe for the other
 * Checkbox call sites (cart, checkout).
 *
 * ─── App addition: `shape` ──────────────────────────────────────────────────────
 *
 * `"square"` (default) is upstream's soft corner; `"round"` makes the box a circle.
 */
import React from "react";

import { cx } from "./cx";
import { Icon } from "./Icon.jsx";

export function Checkbox({
  checked = false,
  indeterminate = false,
  onChange,
  label,
  description,
  size = "md",
  shape = "square",
  disabled = false,
  id,
  style = {},
  className = "",
  ...rest
}) {
  const reactId = React.useId();
  const checkboxId = id || reactId;
  const isOn = checked || indeterminate;
  const isSmall = size === "sm";

  return (
    <label
      htmlFor={checkboxId}
      style={style}
      className={cx(
        "dnd-check relative inline-flex items-start font-sans select-none",
        isSmall ? "gap-[var(--space-2)]" : "gap-[var(--space-3)]",
        disabled ? "cursor-not-allowed opacity-[0.55]" : "cursor-pointer",
        isOn && "is-checked",
        className
      )}
    >
      <input
        id={checkboxId}
        type="checkbox"
        className="peer absolute h-0 w-0 opacity-0"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange && onChange(event.target.checked, event)}
        {...rest}
      />
      <span
        className={cx(
          "dnd-check__box relative inline-flex shrink-0 items-center justify-center border-[1.5px] text-white",
          isSmall
            ? "mt-0 h-[18px] w-[18px] rounded-[6px]"
            : "mt-[1px] h-[22px] w-[22px] rounded-[7px]",
          shape === "round" && "!rounded-full",
          "[transition:background_var(--duration-fast)_var(--ease-standard),border-color_var(--duration-fast)_var(--ease-standard),box-shadow_var(--duration-fast)_var(--ease-standard),transform_var(--duration-base)_var(--ease-spring)]",
          "peer-focus-visible:[box-shadow:var(--focus-ring-accent)]",
          isOn
            ? "border-orange-500 [background:linear-gradient(135deg,var(--orange-400),var(--orange-600))] shadow-[0_2px_6px_-1px_rgba(245,130,32,0.45)] active:scale-[0.88]"
            : "border-line-strong bg-card shadow-[inset_0_1px_2px_rgba(16,38,59,0.07)]",
          !disabled &&
            !isOn &&
            "hover:border-orange-500 hover:shadow-[0_0_0_4px_var(--orange-50)]",
          !disabled &&
            isOn &&
            "hover:shadow-[0_2px_10px_-1px_rgba(245,130,32,0.6)]"
        )}
      >
        <Icon
          name={indeterminate ? "remove" : "check"}
          size={isSmall ? 13 : 16}
          weight={700}
          className={cx(
            "[transition:transform_var(--duration-base)_var(--ease-spring)]",
            isOn ? "scale-100" : "scale-0"
          )}
        />
      </span>
      {(label || description) && (
        <span className="flex flex-col gap-[2px]">
          {label && (
            <span
              className={cx(
                "leading-[1.3] text-ink",
                isSmall ? "text-xs" : "text-base"
              )}
            >
              {label}
            </span>
          )}
          {description && (
            <span
              className={cx("text-ink-2", isSmall ? "text-2xs" : "text-sm")}
            >
              {description}
            </span>
          )}
        </span>
      )}
    </label>
  );
}
